import { ConnectionInfo } from "@app/js/modules/connectionsDrawer";
import { forEach } from "lodash";
import { Dictionary, Models } from "../../types";
import { Organigrama, OrganigramaState } from "../../types/store/organigrama";

export const emptyTotalCost: Organigrama.TotalCost = {
  plantilla: 0,
  desplazamientos: {
    particulares: 0,
    empresa: 0,
  },
  total: 0,
  errors: {
    musico: [],
    vehiculo: [],
  },
};

export const emptyState: OrganigramaState = {
  organigramas: [],
  connections: {},
  paths: {},
  servicios: [],
  errors: {
    musico: [],
    vehiculo: [],
  },
  totalCost: emptyTotalCost,
  cost: {
    costoMusicoFecha: [],
    vehiculosEmpresa: [],
  },
  filteredIds: null,
  selectedIds: [],
  focusedId: undefined,
  selected: {
    musico: undefined,
    instrumento: undefined,
  },
  musicosList: {
    open: false,
  },
  sorter: "-inServicio",
  mode: undefined,
  blocked: undefined,
  blockedBy: undefined,
  fetching: {
    costs: false,
  },
};

export const calculateTotalCost = (
  state: OrganigramaState,
): Organigrama.TotalCost => {
  let cost: Organigrama.TotalCost = {
    plantilla: 0,
    desplazamientos: {
      particulares: 0,
      empresa: 0,
    },
    total: 0,
    errors: {
      musico: [],
      vehiculo: [],
    },
  };

  cost = evaluateCosts(state, cost);

  const serviciosPorMusico = groupServiciosPorMusico(state.servicios);
  evaluateDesplazamientosErrors(serviciosPorMusico, state, cost);

  const serviciosPorVehiculo = groupServiciosPorVehiculo(state.servicios);
  evaluateVehiculosErrors(serviciosPorVehiculo, state, cost);

  return cost;
};

export const evaluateCosts = (
  state: OrganigramaState,
  cost: Organigrama.TotalCost,
) => {
  cost.plantilla = state.cost.costoMusicoFecha
    ? state.cost.costoMusicoFecha
      .map((el) => el.costoServicios)
      .reduce((acc, curr) => acc + curr, 0)
    : NaN;
  cost.desplazamientos.particulares = state.cost.costoMusicoFecha
    ? state.cost.costoMusicoFecha
      .map((el) => el.costoDesplazamientos)
      .reduce((acc, curr) => acc + curr, 0)
    : NaN;
  cost.desplazamientos.empresa = state.cost.costoMusicoFecha
    ? state.cost.vehiculosEmpresa
      .map((el) => el.costoDesplazamientos)
      .reduce((acc, curr) => acc + curr, 0)
    : NaN;
  cost.total = cost.plantilla +
    cost.desplazamientos.particulares +
    cost.desplazamientos.empresa;

  return cost;
};

export const groupServiciosPorMusico = (servicios: Models.Servicio[]) => {
  const serviciosPorMusico: Dictionary<Models.Servicio[]> = {};

  servicios.forEach((servicio) => {
    servicio.integrantes.forEach((musico) => {
      if (serviciosPorMusico[musico.id] === undefined) {
        serviciosPorMusico[musico.id] = [];
      }
      serviciosPorMusico[musico.id].push(servicio);
    });
  });

  return serviciosPorMusico;
};

export const groupServiciosPorVehiculo = (servicios: Models.Servicio[]) => {
  const serviciosPorVehiculo: Dictionary<Models.Servicio[]> = {};

  servicios.forEach((servicio) => {
    servicio.integrantes.forEach((integrante) => {
      if (integrante.pivot.vehiculo_seleccionado) {
        if (
          serviciosPorVehiculo[integrante.pivot.vehiculo_seleccionado.id] ===
          undefined
        ) {
          serviciosPorVehiculo[integrante.pivot.vehiculo_seleccionado.id] = [];
        }
        serviciosPorVehiculo[integrante.pivot.vehiculo_seleccionado.id].push(
          servicio,
        );
      }
    });
  });

  return serviciosPorVehiculo;
};

const evaluateDesplazamientosErrors = (
  serviciosPorMusico: Dictionary<Models.Servicio[]>,
  state: OrganigramaState,
  cost: Organigrama.TotalCost,
) => {
  forEach(serviciosPorMusico, (servicios, idMusico) => {
    servicios.reduce((accumulated, servicio, index, servicios) => {
      if (servicios[index - 1]) {
        const dm = distanceBetween(state, servicios[index - 1].id, servicio.id);
        if (dm) {
          if (dm.errors) {
            pushCostError(cost, idMusico, servicio, dm.errors);
          }
        }
      }
      return accumulated;
    }, 0);
  });
};

const pushCostError = (
  cost: Organigrama.TotalCost,
  idMusico: string,
  servicio: Models.Servicio,
  error: Organigrama.ErrorObject,
): void => {
  cost.errors.musico.push({
    idMusico: parseInt(idMusico, 10),
    servicio,
    error: error,
  });
};

const evaluateVehiculosErrors = (
  serviciosPorVehiculo: Dictionary<Models.Servicio[]>,
  state: OrganigramaState,
  cost: Organigrama.TotalCost,
) => {
  forEach(serviciosPorVehiculo, (servicios, idVehiculo) => {
    servicios.reduce((accumulated, servicio, index, servicios) => {
      if (servicios[index - 1]) {
        if (servicios[index - 1].id !== servicio.id) {
          const dm = distanceBetween(
            state,
            servicios[index - 1].id,
            servicio.id,
          );
          if (dm) {
            if (dm.errors) {
              pushVehiculoError(cost, idVehiculo, servicio, dm.errors);
            }
          }
        }
      }
      return accumulated;
    }, 0);
  });
};

const pushVehiculoError = (
  cost: Organigrama.TotalCost,
  idVehiculo: string,
  servicio: Models.Servicio,
  error: Organigrama.ErrorObject,
): void => {
  cost.errors.vehiculo.push({
    idVehiculo: parseInt(idVehiculo, 10),
    servicio,
    error: error,
  });
};

const distanceBetween = (
  state: OrganigramaState,
  idServicioA: number,
  idServicioB: number,
): ConnectionInfo | undefined => {
  try {
    return state.connections[idServicioA][idServicioB];
  } catch (error) {
    return undefined;
  }
};
